import { Link } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { useEffect, useState, useCallback } from "react";

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

import { Users, UserCheck, File, Eye } from "lucide-react";

import fetchMethodRequest from "../../../../config/service";
import config from "../../../../config/config";
import apiCalls from "../../../../config/apiCalls";
import Loader from "../../../App/Loader";
import FilesAnalysis from "../../FilesAnalysis/components/FilesAnalysis";

const HomeNews = () => {
  /* -------------------- DASHBOARD STATE -------------------- */
  const [dashboardData, setDashboardData] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [filesData, setFilesData] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  /* -------------------- TABLE STATE -------------------- */
  const [recentData, setRecentData] = useState([]);
  const [mostViewedData, setMostViewedData] = useState([]);
  const [loading, setLoading] = useState({ recent: true, mostViewed: true });
  const [error, setError] = useState(null);

  /* -------------------- FETCH DASHBOARD -------------------- */
  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [dashboard, activity, filesObj] = await Promise.all([
          fetchMethodRequest("GET", apiCalls.dashboardData),
          fetchMethodRequest("GET", apiCalls.activityGraph),
          fetchMethodRequest("GET", apiCalls.filesGraph)
        ]);

        const files = Object.entries(filesObj || {}).map(
          ([userName, count]) => ({ userName, count })
        );

        setDashboardData(dashboard);
        setActivityData(activity);
        setFilesData(files);
      } catch (e) {
        console.error(e);
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  /* -------------------- FETCH TABLE DATA -------------------- */
  const fetchData = useCallback(async (type, sortField, direction) => {
    const filter = {
      limit: 20,
      page: 1,
      criteria: [],
      sortfield: sortField,
      direction
    };

    try {
      const res = await fetchMethodRequest(
        "GET",
        `views?filter=${JSON.stringify(filter)}`
      );

      const processed = res.views.slice(0, 10).map((v, i) => ({
        ...v,
        sno: i + 1
      }));

      if (type === "recent") setRecentData(processed);
      else setMostViewedData(processed);

      setLoading(prev => ({ ...prev, [type]: false }));
    } catch (e) {
      setError("Failed to load data");
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  }, []);

  useEffect(() => {
    fetchData("recent", "updated", "desc");
    fetchData("mostViewed", "count", "desc");
  }, [fetchData]);

  /* -------------------- HELPERS -------------------- */
  const sendRecentViewedData = async item => {
    await fetchMethodRequest("POST", "files/recentlyViewed", {
      path: item?.path || item?.fileId?.path
    });
  };

  const getFileLink = item => {
    const path = item?.path || item?.fileId?.path;
    if (!path) return "#";
    if (item?.fileId?.type === "docx") {
      return `https://docs.google.com/viewer?url=${config.imgUrl}${path}`;
    }
    return `${config.imgUrl}${path}`;
  };

  const fileNameTemplate = row => (
    <Link
      to={getFileLink(row)}
      target="_blank"
      onClick={() => sendRecentViewedData(row)}
      className="file-link"
      style={{ fontSize: '0.75rem' }}
    >
      {row?.fileId?.name || "Untitled"}
    </Link>
  );

  /* -------------------- CHARTS -------------------- */
  const FilesChart = () => (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={filesData}>
        <defs>
          <linearGradient id="filesGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="rgba(13,110,253,0.1)" />
            <stop offset="95%" stopColor="rgba(13,110,253,0.02)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
        <XAxis
          dataKey="userName"
          fontSize={10}
          tick={{ fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={{ stroke: '#e0e0e0' }}
        />
        <YAxis
          fontSize={10}
          tick={{ fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={{ stroke: '#e0e0e0' }}
        />
        <Tooltip
          contentStyle={{
            fontSize: '10px',
            padding: '4px 8px',
            borderRadius: '4px'
          }}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="#0d6efd"
          strokeWidth={1.5}
          fill="url(#filesGradient)"
          dot={{ stroke: '#0d6efd', strokeWidth: 1, r: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  const ActivityChart = () => (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={activityData}>
        <defs>
          <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="rgba(25,135,84,0.1)" />
            <stop offset="95%" stopColor="rgba(25,135,84,0.02)" />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="2 2" stroke="#f0f0f0" />
        <XAxis
          dataKey="userName"
          fontSize={10}
          tick={{ fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={{ stroke: '#e0e0e0' }}
        />
        <YAxis
          fontSize={10}
          tick={{ fill: '#666' }}
          axisLine={{ stroke: '#e0e0e0' }}
          tickLine={{ stroke: '#e0e0e0' }}
        />
        <Tooltip
          contentStyle={{
            fontSize: '10px',
            padding: '4px 8px',
            borderRadius: '4px'
          }}
        />
        <Area
          type="monotone"
          dataKey="count"
          stroke="#198754"
          strokeWidth={1.5}
          fill="url(#activityGradient)"
          dot={{ stroke: '#198754', strokeWidth: 1, r: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  /* -------------------- STAT CARD -------------------- */
  /* -------------------- STAT CARD (Side by side layout) -------------------- */
  const StatCard = ({ icon: Icon, title, value, bg }) => (
    <div className="col">
      <div className="stat-card">
        <div className="d-flex align-items-center justify-content-between">
          {/* Left side: Icon and Title */}
          <div className="d-flex align-items-center">
            <div className={`stat-icon ${bg} me-3`}>
              <Icon size={20} className="text-white" />
            </div>
            <div className="d-flex flex-column">
              <div className="small" style={{ fontSize: '0.7rem' }}>{title}</div>
              <div className="fw-bold" style={{ fontSize: '1.1rem' }}>{value || 0}</div>
            </div>
          </div>

          {/* Optional: You could add an arrow or trend indicator here */}
          {/* <div className="text-muted">
          <ChevronRight size={16} />
        </div> */}
        </div>
      </div>
    </div>
  );
  /* -------------------- TABLE CONFIG -------------------- */
  const tableConfigs = [
    {
      key: "recent",
      title: "Recently Viewed Files",
      data: recentData,
      loading: loading.recent,
      columns: [
        {
          field: "sno",
          header: "S.No",
          style: { width: "50px", fontSize: '0.75rem' }
        },
        {
          field: "fileId.name",
          header: "File Name",
          body: fileNameTemplate,
          style: { fontSize: '0.75rem' }
        }
      ]
    },
    {
      key: "mostViewed",
      title: "Most Accessed Files",
      data: mostViewedData,
      loading: loading.mostViewed,
      columns: [
        {
          field: "sno",
          header: "S.No",
          style: { width: "50px", fontSize: '0.75rem' }
        },
        {
          field: "fileId.name",
          header: "File Name",
          body: fileNameTemplate,
          style: { fontSize: '0.75rem' }
        },
        {
          field: "count",
          header: "Views",
          style: { width: "70px", fontSize: '0.75rem' }
        }
      ]
    }
  ];

  /* -------------------- TABLE RENDERER -------------------- */
  const renderTable = cfg => (
    <div className="col-12 col-lg-6" key={cfg.key}>
      <div className="table-card">
        <h6 className="mb-3 text-dark" style={{ fontSize: '0.875rem', fontWeight: '600' }}>{cfg.title}</h6>

        {cfg.loading ? (
          <div className="d-flex justify-content-center py-5">
            <ProgressSpinner style={{ width: '30px', height: '30px' }} />
          </div>
        ) : (
          <div className="table-container">
            <DataTable
              value={cfg.data}
              scrollable
              scrollHeight="180px"
              emptyMessage="No data available"
              className="small-table"
            >
              {cfg.columns.map((col, i) => (
                <Column
                  key={i}
                  field={col.field}
                  header={col.header}
                  body={col.body}
                  style={col.style}
                />
              ))}
            </DataTable>
          </div>
        )}
      </div>
    </div>
  );

  /* -------------------- RENDER -------------------- */
  return (
    <div className="dashboard-container">
      <h3 className="fw-bold mb-4" style={{ fontSize: '1.25rem' }}>Dashboard</h3>

      <Loader loader={dashboardLoading} />

      {/* STATS */}
      <div className="row row-cols-1 row-cols-md-3 g-3 mb-4">
        <StatCard icon={Users} title="Total Users" value={dashboardData?.totalUsers} bg="bg-primary" />
        <StatCard icon={UserCheck} title="Active Users" value={dashboardData?.activeUsers} bg="bg-success" />
        <StatCard icon={File} title="Total Files" value={dashboardData?.totalFiles} bg="bg-info" />
      </div>

      {/* CHARTS */}
      <div className="row g-3 mb-4 p-1">
        {filesData.length > 0 && (
          <div className="col-12 col-lg-6">
            <div className="chart-card">
              <h6 className="mb-3" style={{ fontSize: '0.875rem' }}>
                <File size={16} style={{ marginRight: '6px' }} />
                Files Viewed by Users
              </h6>
              <FilesChart />
            </div>
          </div>
        )}

        {activityData.length > 0 && (
          <div className="col-12 col-lg-6">
            <div className="chart-card">
              <h6 className="mb-3" style={{ fontSize: '0.875rem' }}>
                <Eye size={16} style={{ marginRight: '6px' }} />
                User Activities
              </h6>
              <ActivityChart />
            </div>
          </div>
        )}
      </div>

      {/* TABLES */}
      <div className="row g-3">
        {tableConfigs.map(renderTable)}
      </div>
      <div className="row g-3 files-analysis-card">
        <FilesAnalysis isHomePage={true} />
      </div>

    </div>
  );
};

export default HomeNews;